import { computed, ref } from 'vue'
import { academyApi } from '@/services/api'
import type { TAcademySummary } from '@/services/academy/types'

// Shared by the drawer badge and the academy pages, which refresh it after a visit or a completed lesson.
const summary = ref<TAcademySummary | null>(null)
let loadedForUserId: string | null = null
let pending: Promise<void> | null = null

export function useAcademySummary() {
  /** Silent on failure: the badge is a hint, never a reason for an error toast. */
  async function refresh() {
    try {
      summary.value = await academyApi.summary().promise
    }
    catch {
      summary.value = null
    }
  }

  /** Once per signed-in user; another user signing in on the same tab gets their own numbers. */
  function loadOnce(userId: string | null | undefined) {
    if (!userId) {
      summary.value = null
      loadedForUserId = null
      return Promise.resolve()
    }
    if (loadedForUserId === userId && pending) return pending
    loadedForUserId = userId
    summary.value = null
    pending = refresh()
    return pending
  }

  /** Lessons new since the previous visit + lessons to see again after a major update. */
  const badgeCount = computed(() => (summary.value ? (summary.value.newCount || 0) + (summary.value.updated || 0) : 0))

  return { summary, badgeCount, refresh, loadOnce }
}
